import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { Case } from 'src/app/core/models/case';
import { ClaimsService, buildClaimOrigination } from 'src/app/core/services/claims.service';
import { UserService } from 'src/app/core/services/user.service';
import { ToastService } from 'angular-toastify';
import { TranslateService } from '@ngx-translate/core';
import { EmailRequiredModalComponent } from 'src/app/shared/components/simple/email-required-modal/email-required-modal.component';

@Component({
  selector: 'app-case-information',
  templateUrl: './case-information.component.html',
  styleUrls: ['./case-information.component.scss'],
})
export class CaseInformationComponent implements OnInit {
  @Input() public case?: Case;

  loading = false;
  alreadyEnrolled = false;
  existingClaimId?: string;

  constructor(
    private cs: ClaimsService,
    private us: UserService,
    private router: Router,
    private toast: ToastService,
    private translateService: TranslateService,
    private dialog: MatDialog
  ) {}

  async ngOnInit() {
    if (!this.case?.id) return;

    try {
      const existing = await this.cs.getMyActiveClaimForCase(this.case.id);
      const max = this.case.max_claims_per_client;
      const allowsMultiple = typeof max === 'number' && max > 1;

      this.alreadyEnrolled = !!existing && !allowsMultiple;
      this.existingClaimId = existing?.id;
    } catch (e) {
      console.warn('[CASE-INFO] Could not check existing claim', e);
      // Safe default: hide "Claim Now" button when enrollment status is unknown
      this.alreadyEnrolled = true;
    }
  }

  goToClaim() {
    if (this.existingClaimId) {
      this.router.navigateByUrl('/client/claim/' + this.existingClaimId);
    }
  }

  async createClaim() {
    try {
      this.loading = true;
      const user = await this.us.getCurrentUser();

      if (!user?.primary_email) {
        this.loading = false;
        const ref = this.dialog.open(EmailRequiredModalComponent, {
          width: '400px',
          restoreFocus: false,
          data: {
            title: this.translateService.instant('EmailRequiredToJoinCaseTitle'),
            message: this.translateService.instant('EmailRequiredToJoinCaseMessage'),
            confirmText: this.translateService.instant('EmailRequiredToJoinCaseSave'),
            cancelButton: this.translateService.instant('Close'),
          },
        });
        const savedEmail = await firstValueFrom(ref.afterClosed());
        if (!savedEmail) return;
      }

      await this.completeClaimCreation(user?.id);
    } catch (error) {
      console.error('ERROR', error);
      this.toast.error(this.translateService.instant('ClaimCreationFailed'));
      this.loading = false;
    }
  }

  private async completeClaimCreation(userId?: string) {
    try {
      this.loading = true;
      const r = await this.cs.create(
        {
          status: 'open',
          case_id: this.case?.id,
        },
        userId ?? null,
        buildClaimOrigination('cp-multi-claim-join-case')
      );

      console.log('CREATED CLAIM', r);

      this.toast.success(
        this.translateService.instant('ClaimCreatedSuccessfully')
      );
      this.router.navigateByUrl(`/client/claim/${r.id}/guide`);
    } catch (error) {
      console.error('ERROR', error);
      this.toast.error(this.translateService.instant('ClaimCreationFailed'));
    } finally {
      this.loading = false;
    }
  }
}
